/**
 * Reserved options an AI column filter carries in its flat `custom_<field>`
 * array next to real field values. "Has value" matches stores with any
 * non-empty value for the field. "No value" matches stores with an empty
 * value or none at all. The API takes them as `custom_field_filters.presence`
 * (or an `ai_column_filters` entry's `presence`), never as values.
 */
export const AI_COLUMN_HAS_VALUE = "__has_value__";
export const AI_COLUMN_NO_VALUE = "__no_value__";

const PRESENCE_OPTIONS = {
  has_value: AI_COLUMN_HAS_VALUE,
  no_value: AI_COLUMN_NO_VALUE,
};

/** The two options as `{ value, label }`, in the order the filters list them. */
export const AI_COLUMN_PRESENCE_OPTIONS = [
  { value: AI_COLUMN_HAS_VALUE, label: "Has value" },
  { value: AI_COLUMN_NO_VALUE, label: "No value" },
];

const PRESENCE_LABELS = new Map(AI_COLUMN_PRESENCE_OPTIONS.map((o) => [o.value, o.label]));

/** Display label for a saved presence ("has_value" or "no_value"), or null. */
export const aiColumnPresenceLabel = (presence) =>
  PRESENCE_LABELS.get(PRESENCE_OPTIONS[presence]) ?? null;

/** Display labels for a flat AI column selection, naming the presence options. */
export const aiColumnSelectionLabels = (selected) =>
  (Array.isArray(selected) ? selected : [selected]).map((v) => PRESENCE_LABELS.get(v) ?? v);

const AI_COLUMN_KEY_PREFIX = "custom_";

/** Whether a column or filter key names an AI column (`custom_<field_name>`). */
export const isAiColumnKey = (key) =>
  typeof key === "string" && key.startsWith(AI_COLUMN_KEY_PREFIX);

/**
 * Title and content for an AI column filter's tag in a "Filters Applied" row,
 * or null when `key` is not an AI column. The title is the column's display
 * name. When no loaded definition gives the key a display name, it is the
 * field name in title case.
 */
export const aiColumnFilterTag = (key, selected, customFieldDefs) => {
  if (!isAiColumnKey(key)) return null;
  const fieldName = key.slice(AI_COLUMN_KEY_PREFIX.length);
  const def = (customFieldDefs || []).find((d) => d?.field_name === fieldName);
  const title =
    def?.display_name ||
    fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  return { title, content: aiColumnSelectionLabels(selected).join(", ") };
};

/** Display labels for a saved-view entry's values and presence. */
export const aiColumnEntryLabels = (values, presence) =>
  aiColumnSelectionLabels(joinAiColumnSelection(values, presence));

/**
 * Split a flat AI column selection into real values and a presence.
 * Returns `{ values, presence }`, where presence is "has_value", "no_value"
 * or null. Returns null when the selection filters nothing: it is empty, or
 * it holds both presence options, which together match every store.
 */
export const splitAiColumnSelection = (selected) => {
  if (!Array.isArray(selected) || selected.length === 0) return null;
  const hasValue = selected.includes(AI_COLUMN_HAS_VALUE);
  const noValue = selected.includes(AI_COLUMN_NO_VALUE);
  if (hasValue && noValue) return null;
  const values = selected.filter((v) => v !== AI_COLUMN_HAS_VALUE && v !== AI_COLUMN_NO_VALUE);
  const presence = hasValue ? "has_value" : noValue ? "no_value" : null;
  if (values.length === 0 && !presence) return null;
  return { values, presence };
};

/**
 * Rebuild the flat selection from saved values and a presence, so a
 * restored filter shows the presence option as selected.
 */
export const joinAiColumnSelection = (values, presence) => {
  const selection = Array.isArray(values) ? [...values] : [];
  const option = PRESENCE_OPTIONS[presence];
  if (option) selection.push(option);
  return selection;
};

/**
 * Build the API's `custom_field_filters` from `{ field_name: selection }`.
 * Returns `{ values, presence }`, with `presence` present only when a field
 * selects one, or null when no field filters anything.
 */
export const buildCustomFieldFilters = (selections) => {
  const values = {};
  const presence = {};
  Object.entries(selections || {}).forEach(([fieldName, selected]) => {
    const split = splitAiColumnSelection(selected);
    if (!split) return;
    if (split.values.length > 0) values[fieldName] = split.values;
    if (split.presence) presence[fieldName] = split.presence;
  });
  if (Object.keys(values).length === 0 && Object.keys(presence).length === 0) {
    return null;
  }
  return Object.keys(presence).length > 0 ? { values, presence } : { values };
};
